import React from 'react'
import clsx from 'clsx'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styles from './Header.module.css'
import { getLandingImageProps } from '../../utils/landingImages'
import { useHomeTheme } from '../../utils/homeTheme'
import WbSunnyOutlinedIcon from '@material-ui/icons/WbSunnyOutlined'
import NightsStayOutlinedIcon from '@material-ui/icons/NightsStayOutlined'
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined'

import Button from '@material-ui/core/Button'
import Drawer from '@material-ui/core/Drawer'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grow from '@material-ui/core/Grow'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Avatar from '@material-ui/core/Avatar'
import { logoutUser } from '../../actions/auth'
import { selectAuthData } from '../../store/selectors/authSelectors'

const Header = () => {
  const dispatch = useDispatch()
  const authData = useSelector(selectAuthData)
  const history = useHistory()
  const location = useLocation()
  const isHome = location.pathname === '/'
  const [homeTheme, toggleHomeTheme] = useHomeTheme(isHome)
  const themeAction = homeTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false)
  const [servicesOpen, setServicesOpen] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const servicesTimer = React.useRef(null)
  const servicesRef = React.useRef(null)
  const servicesLinkRef = React.useRef(null)
  const servicesFocusIndex = React.useRef(null)
  const mobileToggleRef = React.useRef(null)
  const anchorRef = React.useRef(null)

  const clearServicesTimer = () => {
    if (servicesTimer.current) {
      clearTimeout(servicesTimer.current)
      servicesTimer.current = null
    }
  }

  const openServices = () => {
    clearServicesTimer()
    setServicesOpen(true)
  }

  const closeServices = () => {
    clearServicesTimer()
    setServicesOpen(false)
  }

  const scheduleCloseServices = () => {
    clearServicesTimer()
    servicesTimer.current = setTimeout(() => {
      if (!servicesRef.current?.contains(document.activeElement)) setServicesOpen(false)
    }, 120)
  }

  const handleServicesKeyDown = (event) => {
    const links = servicesRef.current?.querySelectorAll('[data-service-link]') || []
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      servicesLinkRef.current?.focus()
      closeServices()
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      openServices()
      const currentIndex = Array.from(links).indexOf(document.activeElement)
      const nextIndex = event.key === 'ArrowDown'
        ? (currentIndex + 1) % links.length
        : (currentIndex <= 0 ? links.length - 1 : currentIndex - 1)
      if (servicesOpen) links[nextIndex]?.focus()
      else servicesFocusIndex.current = nextIndex
    }
  }

  React.useEffect(() => {
    if (servicesOpen && servicesFocusIndex.current !== null) {
      servicesRef.current?.querySelectorAll('[data-service-link]')[servicesFocusIndex.current]?.focus()
      servicesFocusIndex.current = null
    }
  }, [servicesOpen])

  React.useEffect(() => () => {
    if (servicesTimer.current) clearTimeout(servicesTimer.current)
  }, [])

  React.useEffect(() => {
    setMobileNavOpen(false)
    setServicesOpen(false)
  }, [location.pathname])

  React.useEffect(() => {
    if (!mobileNavOpen) return undefined
    const closeOnDesktop = () => {
      if (window.innerWidth > 767) setMobileNavOpen(false)
    }
    window.addEventListener('resize', closeOnDesktop)
    return () => {
      window.removeEventListener('resize', closeOnDesktop)
    }
  }, [mobileNavOpen])

  const logout = () => {
    setOpen(false)
    dispatch(logoutUser())
    history.push('/')
  }

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return
    setOpen(false)
  }

  const openLink = (link) => {
    history.push(`/${link}`)
    setOpen(false)
  }

  const handleListKeyDown = (event) => {
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
    }
  }

  const prevOpen = React.useRef(open)
  React.useEffect(() => {
    if (prevOpen.current && !open) anchorRef.current?.focus()
    prevOpen.current = open
  }, [open])

  return (
    <>
      <header className={clsx(authData ? styles.header : styles.header2, isHome && styles.homeHeader)}>
        <Link className={styles.logoLink} to={authData ? '/dashboard' : '/'} aria-label={authData ? 'Wholesale USA dashboard' : 'Wholesale USA home'}>
          <img className={clsx(styles.logo, isHome && styles.lightLogo)} width="50" height="50" src="/images/wholesale_shield.PNG" {...getLandingImageProps('/images/wholesale_shield.PNG', '50px')} alt="Wholesale USA" />
          {isHome && <img className={clsx(styles.logo, styles.darkLogo)} width="50" height="50" src="/images/wholesale_shield.PNG" {...getLandingImageProps('/images/wholesale_shield.PNG', '50px')} alt="" aria-hidden="true" />}
        </Link>
        {isHome && (
          <nav className={styles.homeLinks} aria-label="Main navigation">
            <a href="#about">About</a>
            <div
              ref={servicesRef}
              className={styles.dropdown}
              onMouseEnter={openServices}
              onMouseLeave={scheduleCloseServices}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) closeServices()
              }}
              onKeyDown={handleServicesKeyDown}
            >
              <a
                ref={servicesLinkRef}
                href="#services"
                className={styles.dropdownLabel}
                aria-expanded={servicesOpen}
                aria-controls="header-services"
                onFocus={openServices}
                onClick={closeServices}
              >Services</a>
              <div id="header-services" className={styles.dropdownMenu} hidden={!servicesOpen}>
                <a data-service-link href="#services-property-managers" onClick={closeServices}>Property Managers</a>
                <a data-service-link href="#services-builders" onClick={closeServices}>Builders</a>
                <a data-service-link href="#services-retail-dealers" onClick={closeServices}>Retail Dealers</a>
              </div>
            </div>
            <a href="#contact">Contact</a>
          </nav>
        )}
        <div className={styles.headerActions}>
          {isHome && (
            <>
              <a className={styles.homeCallButton} href="tel:15167996404">Call now</a>
              <button className={styles.themeToggle} type="button" onClick={toggleHomeTheme} aria-label={themeAction} title={themeAction}>
                <NightsStayOutlinedIcon className={styles.darkModeIcon} aria-hidden="true" />
                <WbSunnyOutlinedIcon className={styles.lightModeIcon} aria-hidden="true" />
              </button>
              <button
                ref={mobileToggleRef}
                className={styles.mobileToggle}
                type="button"
                onClick={() => setMobileNavOpen((previous) => !previous)}
                aria-label="Toggle navigation"
                aria-expanded={mobileNavOpen}
                aria-controls="header-mobile-navigation"
              >
                <span />
                <span />
                <span />
              </button>
            </>
          )}
          {!authData && !isHome && <button onClick={() => history.push('/login')} className={styles.login}>Get started</button>}
          {authData && (
            <div className={styles.accountMenu}>
              <Button
                ref={anchorRef}
                aria-label="Account menu"
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={() => setOpen((previous) => !previous)}
              >
                <Avatar style={{ backgroundColor: '#1976D2' }}>{authData?.result?.name?.charAt(0)}</Avatar>
              </Button>
              <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
                {({ TransitionProps, placement }) => (
                  <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                    <Paper elevation={3}>
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList autoFocusItem={open} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                          <MenuItem onClick={() => openLink('dashboard')}>Dashboard</MenuItem>
                          <MenuItem onClick={() => openLink('settings')}>{authData?.result?.name?.split(' ')[0]}</MenuItem>
                          <MenuItem onClick={logout}>Logout</MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </div>
          )}
        </div>
      </header>
      {isHome && (
        <Drawer
          anchor="right"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          className={styles.mobileDrawer}
          classes={{ paper: styles.mobileNav }}
          BackdropProps={{ className: styles.mobileNavOverlay }}
          PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-label': 'Mobile navigation' }}
          transitionDuration={{ enter: 280, exit: 220 }}
        >
          <div className={styles.mobileNavTop}>
            <button
              className={styles.mobileNavClose}
              type="button"
              aria-label="Close navigation"
              autoFocus
              onClick={() => setMobileNavOpen(false)}
            >
              <CloseOutlinedIcon aria-hidden="true" />
            </button>
          </div>
          <nav
            id="header-mobile-navigation"
            className={styles.mobileNavLinks}
            aria-label="Mobile navigation"
          >
            <a href="#about" onClick={() => setMobileNavOpen(false)}>About</a>
            <a href="#services" onClick={() => setMobileNavOpen(false)}>Services</a>
            <a className={styles.mobileServiceLink} href="#services-property-managers" onClick={() => setMobileNavOpen(false)}>Property Managers</a>
            <a className={styles.mobileServiceLink} href="#services-builders" onClick={() => setMobileNavOpen(false)}>Builders</a>
            <a className={styles.mobileServiceLink} href="#services-retail-dealers" onClick={() => setMobileNavOpen(false)}>Retail Dealers</a>
            <a href="#contact" onClick={() => setMobileNavOpen(false)}>Contact</a>
          </nav>
        </Drawer>
      )}
    </>
  )
}

export default Header
